/**
 * Extracts a user-friendly error message from an RTK Query error.
 * - Token expiration → friendly copy.
 * - Other FetchBaseQueryError with server message → pass through error.data.message.
 * - No message available → return fallback.
 */

export const TOKEN_EXPIRED_MESSAGE =
  "Your session has expired. Please refresh the page to sign in again.";

export const EXPIRED_TOKEN_NOTIFICATION_ID = "expired-token";

const hasDataMessage = (
  error: unknown,
): error is { data: { message: string } } =>
  typeof error === "object" &&
  error !== null &&
  "data" in error &&
  typeof (error as { data: unknown }).data === "object" &&
  (error as { data: unknown }).data !== null &&
  "message" in (error as { data: { message: unknown } }).data &&
  typeof (error as { data: { message: string } }).data.message === "string";

/**
 * Returns true if the error represents an expired authentication token.
 * Uses case-insensitive substring match on "incoming token has expired".
 */
export const isExpiredTokenError = (error: unknown): boolean => {
  if (!hasDataMessage(error)) return false;
  return error.data.message
    .toLowerCase()
    .includes("incoming token has expired");
};

export const getQueryErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (isExpiredTokenError(error)) {
    return TOKEN_EXPIRED_MESSAGE;
  }
  if (!hasDataMessage(error)) {
    return fallback;
  }
  const serverMessage = error.data.message;
  return serverMessage.length > 0 ? serverMessage : fallback;
};
